"use client";

import * as React from "react";

import { cn } from "@rivval/css";

const variants = {
  primary: {
    content: "bg-cyan-400",
    text: "text-black",
    label: "bg-cyan-400 border-l border-blue-400",
    glitch:
      "bg-cyan-400 [filter:drop-shadow(-2px_3px_#67e3f3)_drop-shadow(-1px_-3px_#02d8f3)_drop-shadow(2px_1px_#02d8f3)]",
  },
  secondary: {
    content: "bg-violet-400",
    text: "text-black",
    label: "bg-white border-l border-blue-400",
    glitch:
      "bg-violet-400 [filter:drop-shadow(-2px_3px_#a78bfa)_drop-shadow(-1px_-3px_#7c3aed)_drop-shadow(2px_1px_#7c3aed)]",
  },
  warning: {
    content: "bg-yellow-400",
    text: "text-black",
    label: "bg-yellow-400 border-l border-blue-400",
    glitch:
      "bg-yellow-400 [filter:drop-shadow(-2px_3px_#fcd34d)_drop-shadow(-1px_-3px_#f59e0b)_drop-shadow(2px_1px_#f59e0b)]",
  },
  destructive: {
    content: "bg-red-400",
    text: "text-white",
    label: "bg-white border-l border-blue-400",
    glitch:
      "bg-red-400 [filter:drop-shadow(-2px_3px_#f87171)_drop-shadow(-1px_-3px_#ef4444)_drop-shadow(2px_1px_#ef4444)]",
  },
} as const;

interface RazorGlitchButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  className?: string;
  variant?: keyof typeof variants;
  label?: string;
  borderColor?: string;
}

export function RazorGlitchButton({
  children,
  className,
  variant = "warning",
  label,
  borderColor,
  ...props
}: RazorGlitchButtonProps) {
  const variantStyles = variants[variant];

  return (
    <button
      className={cn(
        "group relative h-[60px] w-[230px] cursor-pointer border-0 font-mono text-sm uppercase outline-hidden",
        "[clip-path:polygon(92%_0,100%_25%,100%_100%,8%_100%,0%_75%,0_0)]",
        className,
      )}
      {...props}
    >
      {/* Border Layer */}
      <div
        className="absolute inset-0 z-0 [clip-path:polygon(92%_0,100%_25%,100%_100%,8%_100%,0%_75%,0_0)]"
        style={{ backgroundColor: borderColor ?? "black" }}
      />

      {/* Content Layer */}
      <div
        className={cn(
          "absolute inset-[2px] z-1 flex items-center justify-center",
          "[clip-path:polygon(92%_0,100%_25%,100%_100%,8%_100%,0%_75%,0_0)]",
          variantStyles.content,
          variantStyles.text,
        )}
      >
        {children}
      </div>

      {/* Label */}
      {label && (
        <div
          className={cn(
            "absolute bottom-[-1px] right-[8%] z-3 px-[5px] font-mono text-[0.4rem] uppercase",
            variantStyles.label,
          )}
        >
          {label}
        </div>
      )}

      {/* Glitch Effect Layer */}
      <div
        className={cn(
          "absolute inset-0 z-2 hidden h-full w-full",
          variantStyles.glitch,
          "group-hover:animate-glitch group-hover:block",
          "group-focus:animate-glitch group-focus:block",
        )}
      />
    </button>
  );
}
